/**
 * Justified rows (Flickr / Google Photos style): photos keep their own aspect ratio and every
 * row is scaled so it spans the container exactly. The last row is never stretched past the
 * target height, so a lone portrait does not balloon into a giant tile.
 *
 * @param {number[]} ratios      width / height of each photo, in display order
 * @param {number}   width       container width in px
 * @param {number}   targetHeight preferred row height in px
 * @param {number}   gap         gap between photos (and rows) in px
 * @returns {{ height: number, items: { index: number, width: number }[], complete: boolean }[]}
 */
export function computeJustifiedRows(ratios, width, targetHeight, gap) {
    if (!width || ratios.length === 0) return [];

    // Guard against broken metadata: clamp to sane panorama/portrait limits.
    const safe = ratios.map((ratio) => Math.min(4, Math.max(0.4, Number(ratio) || 1)));
    const rows = [];
    let current = [];
    let ratioSum = 0;

    const flush = (complete) => {
        const gaps = gap * (current.length - 1);
        const fittedHeight = (width - gaps) / ratioSum;
        // A complete row fills the width exactly; the trailing row keeps the target height.
        const height = complete ? fittedHeight : Math.min(fittedHeight, targetHeight);
        rows.push({
            height,
            complete,
            items: current.map((index) => ({ index, width: safe[index] * height })),
        });
        current = [];
        ratioSum = 0;
    };

    const fittedHeight = (count, sum) => (width - gap * (count - 1)) / sum;

    safe.forEach((ratio, index) => {
        current.push(index);
        ratioSum += ratio;
        const naturalWidth = ratioSum * targetHeight + gap * (current.length - 1);
        if (naturalWidth < width) return;

        // The row is full. Close it with or without this photo — whichever lands closer to
        // the target height — so rows don't come out squat from always over-filling.
        const withPhoto = fittedHeight(current.length, ratioSum);
        const withoutPhoto = current.length > 1 ? fittedHeight(current.length - 1, ratioSum - ratio) : Infinity;
        if (Math.abs(withoutPhoto - targetHeight) < Math.abs(withPhoto - targetHeight)) {
            current.pop();
            ratioSum -= ratio;
            flush(true);
            current.push(index);
            ratioSum += ratio;
            if (ratio * targetHeight >= width) flush(true);
        } else {
            flush(true);
        }
    });

    if (current.length) {
        // A short trailing row (an "orphan") looks unfinished. Fold it into the row above when
        // that keeps the merged row from getting too squat; otherwise leave it left-aligned.
        const trailingWidth = ratioSum * targetHeight + gap * (current.length - 1);
        const previous = rows[rows.length - 1];
        if (previous && trailingWidth < width * ORPHAN_FILL) {
            const merged = [...previous.items.map((item) => item.index), ...current];
            const mergedRatio = merged.reduce((sum, index) => sum + safe[index], 0);
            const mergedHeight = (width - gap * (merged.length - 1)) / mergedRatio;
            if (mergedHeight >= targetHeight * MIN_MERGED_HEIGHT) {
                rows.pop();
                current = merged;
                ratioSum = mergedRatio;
                flush(true);
                return rows;
            }
        }
        flush(false);
    }
    return rows;
}

const ORPHAN_FILL = 0.6;
const MIN_MERGED_HEIGHT = 0.75;

/** Row height that suits the container width (shorter rows on phones). */
export function targetRowHeight(width) {
    if (width < 480) return 150;
    if (width < 900) return 210;
    return 280;
}
